// Global keystroke listener that detects barcode scanners running in
// HID (keyboard-emulated) mode — the standard $5-80 Bluetooth/USB scanner.
//
// A scanner types the whole code very fast then sends Enter (some send Tab).
// We buffer keystrokes and, when a terminator arrives, classify the burst:
// it's a scan if it's long enough AND every char came in fast succession.
// Human typing has irregular, slower gaps, so it never qualifies.
//
// Design notes (why the rewrite — cheap Taobao BLE scanners):
//  • SLOW-TOLERANT: BLE HID scanners can take 60-120ms PER character. The old
//    30-50ms gap reset truncated their codes → garbled / dropped scans. We now
//    tolerate up to SCAN_MAX_GAP per char.
//  • FOCUS-INDEPENDENT: we buffer no matter where focus is (the old code bailed
//    whenever focus sat in any field other than the POS search box, so a scan
//    "did nothing" unless you first tapped the search box). We only act on a
//    CONFIRMED scan, so manual typing is never hijacked.
//  • NON-INTRUSIVE: we don't preventDefault individual characters, so typing in
//    the search box still works; we only swallow the terminator of a real scan.
//
// onScan(code, target) is called when a scan burst is confirmed.

const SCAN_MAX_GAP = 120;   // fast-burst threshold: max ms/char for a "definite" scan
const SLOW_MAX_GAP = 400;   // slow-tolerant ceiling for barcode-SHAPED bursts (sleepy BLE scanners)
const IDLE_RESET   = 550;   // ms of silence → drop a partial buffer
const MIN_LENGTH   = 4;     // ignore short bursts (likely human typing)
const BARCODE_MIN  = 8;     // length above which a clean alnum burst is treated as a barcode
const BARCODE_RE   = /^[0-9A-Za-z._\-]+$/; // no spaces/punctuation → looks like a scanned code

export function startBarcodeListener(onScan) {
  let buf = '';
  let lastT = 0;
  let maxGap = 0;             // largest inter-char gap seen in the current burst
  let resetTimer = null;

  const reset = () => {
    buf = ''; lastT = 0; maxGap = 0;
    if (resetTimer) { clearTimeout(resetTimer); resetTimer = null; }
  };

  const handler = (e) => {
    if (e.isComposing || e.metaKey || e.ctrlKey || e.altKey) return;

    const now = performance.now();
    const gap = lastT ? now - lastT : 0;

    // Terminator — a scanner ends with Enter (some with Tab).
    if (e.key === 'Enter' || e.key === 'Tab') {
      // A scan is EITHER a tight fast burst (short codes), OR a "barcode-shaped"
      // burst (≥8 clean alnum chars, no spaces) that came in a bit slower — this
      // second path rescues the first scan after a BLE scanner wakes from sleep,
      // whose characters arrive slowly. Human typing rarely produces an 8+ char
      // space-free token terminated by Enter, and if it does it just runs a
      // harmless search, so the looser rule is safe.
      const fastBurst = maxGap <= SCAN_MAX_GAP;
      const barcodeShaped = buf.length >= BARCODE_MIN && BARCODE_RE.test(buf) && maxGap <= SLOW_MAX_GAP;
      const looksLikeScan = buf.length >= MIN_LENGTH && gap <= IDLE_RESET && (fastBurst || barcodeShaped);
      if (looksLikeScan) {
        // Confirmed scan → swallow the terminator so it doesn't submit a form
        // or move focus, and route the code to the cart logic.
        e.preventDefault();
        e.stopPropagation();
        const code = buf;
        const target = e.target;
        reset();
        // The individual characters were NOT preventDefault'd (so manual typing
        // keeps working), so a scan made WHILE a text field is focused leaks the
        // code into that field (e.g. the search box). Now that the burst is
        // CONFIRMED as a scan, strip the leaked code back out. Guarded by
        // endsWith(code) so we only touch the field the scan actually leaked
        // into, and we keep any text the user had typed before scanning. Uses
        // the native value setter + an 'input' event so React controlled inputs
        // (which own .value) pick up the cleared value via their onChange.
        try {
          if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
              && typeof target.value === 'string' && code && target.value.endsWith(code)) {
            const proto = target.tagName === 'INPUT' ? window.HTMLInputElement.prototype : window.HTMLTextAreaElement.prototype;
            const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
            if (setter) {
              setter.call(target, target.value.slice(0, target.value.length - code.length));
              target.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
        } catch (err) { console.warn('[scanner] strip leak failed', err); }
        try { onScan(code, target); } catch (err) { console.error('[scanner]', err); }
        return;
      }
      // Not a scan → let the field handle its own Enter/Tab.
      reset();
      return;
    }

    // Visible single character — accumulate.
    if (e.key.length === 1) {
      if (buf && gap > IDLE_RESET) reset();       // long pause → start a fresh burst
      if (buf) maxGap = Math.max(maxGap, gap);    // track worst gap to classify on Enter
      buf += e.key;
      lastT = now;
      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = setTimeout(reset, IDLE_RESET + 100);
      // NOTE: intentionally no preventDefault here — manual typing in the POS
      // search box must keep working. A scan into a non-search field may leak a
      // couple of characters there; scanning is meant to be done on the POS
      // screen where the global listener routes it to the cart.
    }
  };

  window.addEventListener('keydown', handler, true);

  // Native (Capacitor iOS) path: barcode arrives via mPOP USB → Bluetooth →
  // StarPrinter plugin 'scan' event. Wire it directly into the same onScan
  // callback so cart-add logic stays in one place.
  let nativeOff = () => {};
  const sp = (typeof window !== 'undefined') ? window.Capacitor?.Plugins?.StarPrinter : null;
  if (sp?.addListener) {
    try {
      const subPromise = sp.addListener('scan', (event) => {
        const code = (event?.code || '').trim();
        if (code.length >= MIN_LENGTH) {
          try { onScan(code, document.activeElement); } catch (err) { console.error('[scanner native]', err); }
        }
      });
      nativeOff = () => { Promise.resolve(subPromise).then(s => s?.remove?.()); };
    } catch (err) { console.warn('[scanner] native addListener failed', err); }
  }

  return () => { window.removeEventListener('keydown', handler, true); nativeOff(); };
}
